import { z } from 'zod';
import { publicUserSchema, timestampSchema, ulidSchema } from './common.js';

export const COMMUNITY_SPACE_KINDS = ['chat', 'forum', 'dm'] as const;
export const communitySpaceKindSchema = z.enum(COMMUNITY_SPACE_KINDS);
export type CommunitySpaceKind = z.infer<typeof communitySpaceKindSchema>;

export const communitySpaceSchema = z.object({
  key: z.string(),
  kind: communitySpaceKindSchema,
  name: z.string(),
  /** Provider-side space id: a chat or forum id, or a DM channel id for `dm`. */
  id: z.string(),
  /** Announcements is read only for everyone but admins. */
  readOnly: z.boolean(),
  gameId: ulidSchema.nullable(),
});
export type CommunitySpace = z.infer<typeof communitySpaceSchema>;

export const communityConfigSchema = z.object({
  spaces: z.array(communitySpaceSchema),
  /** The user's crew group DM, when they are in a crew with at least one guest. */
  crewDmChannelId: z.string().nullable(),
  communityId: z.string(),
  /** `embedded`, `window` or `browser`, decided per platform (VERIFY-6). */
  embedMode: z.enum(['embedded', 'window', 'browser']),
});
export type CommunityConfig = z.infer<typeof communityConfigSchema>;

export const communityTokenSchema = z.object({
  token: z.string(),
  expiresAt: timestampSchema,
});
export type CommunityToken = z.infer<typeof communityTokenSchema>;

export const forumPostSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  content: z.string(),
  author: publicUserSchema.nullable(),
  authorIsStaff: z.boolean(),
  createdAt: timestampSchema,
  replyCount: z.number().int(),
  pinned: z.boolean(),
});
export type ForumPost = z.infer<typeof forumPostSchema>;

export const createForumPostBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  content: z.string().trim().min(1).max(20000),
});

export const createForumReplyBodySchema = z.object({
  content: z.string().trim().min(1).max(20000),
});

/** DM conversation shown by our own DMs list (the docs describe no DMs List element). */
export const dmChannelSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  lastMessageAt: timestampSchema.nullable(),
  members: z.array(publicUserSchema),
});
export type DmChannel = z.infer<typeof dmChannelSchema>;
